import { compose } from 'recompose';
import { Context, ContextProvider } from './store';
import React, { useContext } from 'react';
import { CircularProgress, Grid, IconButton, TextField, Tooltip } from '@material-ui/core';
import { css } from '@emotion/react';
import Snackbar from '../Snackbar';
import { useClientUser } from '@/hooks';
import { carrierDisplayName } from '@/utilities/carrierAlias';
import { CLAIM_STATUSES, CLAIM_TYPES } from '@/constants/claims';
import { useColumns } from './columns';
import { colors } from '@/styles';
import { OnwardTab, OnwardTabContainer } from '../Tabs';
import { Title } from './blocks';
import SearchInput from '../misc/SearchInput';
import { MenuItem } from '@material-ui/core';
import { format } from 'date-fns';
import { asBrowserDate } from '@/utilities/convertToISO';
import EditClaimModal from './modals/EditClaimModal';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { UserContext } from '../App';
import { useNavigate } from 'react-router';
import Table from '../Table';
import { Body1 } from './blocks';

const Claims = () => {
    const { user } = useContext(UserContext);
    const { state, callbacks } = useContext(Context);
    // Aliased alongside the Carrier column; the value stays the real client_id so the
    // filter itself is unchanged.
    const { isOnwardAdmin, accountType, partnerCarriersById } = useClientUser();
    const navigate = useNavigate();

    const COLUMNS = useColumns({ accountType, claimStatus: state.filters.claimStatus });

    return (
        <Grid
            container
            direction="column"
            css={css`
                height: 100%;
                flex-wrap: nowrap;
            `}
        >
            <Snackbar
                open={!!state.notification.message}
                {...state.notification}
                handleClose={() => callbacks.setNotification({})}
            />
            <EditClaimModal
                open={!!state.newClaim}
                claim={state.newClaim}
                onClose={() => callbacks.setNewClaim(null)}
                onSave={(claim) => {
                    callbacks.setNewClaim(null);
                    navigate(`/claims/${claim.claim_id}`);
                }}
                callbacks={{
                    setNotification: callbacks.setNotification,
                }}
            />
            <Grid
                container
                direction="row"
                css={css`
                    padding: 0rem 2rem;
                    border-bottom: 1px solid ${colors.greys.border};
                    background: ${colors.white.primary};
                `}
            >
                <Grid item xs={2}></Grid>
                <Grid
                    item
                    xs={8}
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: space-around;
                    `}
                >
                    <OnwardTabContainer
                        value={state.filters.claimStatus || 'ALL'}
                        onChange={(_, tab) => callbacks.filter('claimStatus', tab === 'ALL' ? null : tab)}
                    >
                        <OnwardTab value="ALL" label="All" />
                        {Object.entries(CLAIM_STATUSES).map(([key, value]) => (
                            <OnwardTab key={key} value={key} label={value} />
                        ))}
                    </OnwardTabContainer>
                </Grid>
                <Grid
                    item
                    xs={2}
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                    `}
                >
                    <Tooltip style={{ color: '#59B863' }} title={'Refresh claims'} placement="top">
                        <IconButton onClick={() => callbacks.refetch()} disabled={state.loading.claims}>
                            <RefreshIcon
                                css={css`
                                    color: ${colors.greens.primary};
                                `}
                            />
                        </IconButton>
                    </Tooltip>
                    <Tooltip style={{ color: '#59B863' }} title={'Create a new claim'} placement="top">
                        <IconButton
                            onClick={() =>
                                callbacks.setNewClaim({
                                    requested_by: user.username,
                                    created_by: user.user_id,
                                })
                            }
                        >
                            <AddCircleIcon
                                css={css`
                                    color: ${colors.greens.primary};
                                `}
                            />
                        </IconButton>
                    </Tooltip>
                </Grid>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    padding: 2rem;
                `}
            >
                <Title>Claims</Title>
            </Grid>
            <Grid
                container
                direction="row"
                css={css`
                    padding: 0rem 2rem;
                `}
            >
                <Grid
                    container
                    direction="row"
                    css={css`
                        padding: 0.5rem;
                        border-radius: 0.5rem;
                        background: ${colors.white.primary};
                        border: 1px solid ${colors.greys.border};
                    `}
                >
                    <Grid
                        item
                        xs={isOnwardAdmin ? 4 : 3}
                        css={css`
                            padding: 0.5rem;
                        `}
                    >
                        <SearchInput
                            placeholder="Search"
                            value={state.filters.search}
                            onChange={(input) => callbacks.filter('search', input)}
                        />
                    </Grid>
                    {isOnwardAdmin ? (
                        <>
                            <Grid
                                item
                                xs={4}
                                css={css`
                                    padding: 0.5rem;
                                `}
                            >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    select
                                    size="small"
                                    label="Shipper"
                                    value={state.filters.shipper || 'ALL'}
                                    onChange={(e) =>
                                        callbacks.filter('shipper', e.target.value === 'ALL' ? null : e.target.value)
                                    }
                                >
                                    <MenuItem value="ALL">All</MenuItem>
                                    {state.shippers.map((shipper) => (
                                        <MenuItem key={shipper.client_id} value={shipper.client_id}>
                                            {shipper.business_name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid
                                item
                                xs={4}
                                css={css`
                                    padding: 0.5rem;
                                `}
                            >
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    select
                                    size="small"
                                    label="Carrier"
                                    value={state.filters.carrier || 'ALL'}
                                    onChange={(e) =>
                                        callbacks.filter('carrier', e.target.value === 'ALL' ? null : e.target.value)
                                    }
                                >
                                    <MenuItem value="ALL">All</MenuItem>
                                    {state.carriers.map((carrier) => (
                                        <MenuItem key={carrier.client_id} value={carrier.client_id}>
                                            {carrierDisplayName(carrier, partnerCarriersById)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        </>
                    ) : null}
                    <Grid
                        item
                        xs={isOnwardAdmin ? 4 : 3}
                        css={css`
                            padding: 0.5rem;
                        `}
                    >
                        <TextField
                            fullWidth
                            variant="outlined"
                            select
                            size="small"
                            label="Claim Type"
                            value={state.filters.claimType || 'ALL'}
                            onChange={(e) =>
                                callbacks.filter('claimType', e.target.value === 'ALL' ? null : e.target.value)
                            }
                        >
                            <MenuItem value="ALL">All</MenuItem>
                            {Object.entries(CLAIM_TYPES).map(([key, value]) => (
                                <MenuItem key={key} value={key}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid
                        item
                        xs={isOnwardAdmin ? 4 : 3}
                        css={css`
                            padding: 0.5rem;
                        `}
                    >
                        <TextField
                            fullWidth
                            type="date"
                            variant="outlined"
                            label="Start Date"
                            size="small"
                            value={
                                state.filters.startDate
                                    ? format(asBrowserDate(state.filters.startDate), 'yyyy-MM-dd')
                                    : ''
                            }
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = new Date(new Date(e.target.value).setUTCHours(0, 0, 0, 0)).toISOString();
                                }
                                callbacks.filter('startDate', dateStr);
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={isOnwardAdmin ? 4 : 3}
                        css={css`
                            padding: 0.5rem;
                        `}
                    >
                        <TextField
                            fullWidth
                            type="date"
                            variant="outlined"
                            label="End Date"
                            size="small"
                            value={
                                state.filters.endDate ? format(asBrowserDate(state.filters.endDate), 'yyyy-MM-dd') : ''
                            }
                            onChange={(e) => {
                                let dateStr = null;
                                if (e.target.value) {
                                    dateStr = new Date(new Date(e.target.value).setUTCHours(0, 0, 0, 0)).toISOString();
                                }
                                callbacks.filter('endDate', dateStr);
                            }}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid
                container
                css={css`
                    padding: 2rem;
                    flex-grow: 1;
                `}
            >
                {state.loading.claims ? (
                    <Grid
                        item
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        `}
                    >
                        <CircularProgress
                            size={200}
                            css={css`
                                color: ${colors.greens.primary};
                            `}
                        />
                    </Grid>
                ) : state.claims?.length ? (
                    <Table
                        columns={COLUMNS}
                        data={state.claims}
                        options={{}}
                        callbacks={{
                            getRowId: (claim) => claim.claim_id,
                            rowOnClick: (e, claim) => {
                                e.preventDefault();
                                const href = `/claims/${claim.claim_id}`;
                                if (e.ctrlKey) {
                                    window.open(href, '_blank');
                                } else {
                                    navigate(href);
                                }
                            },
                            getRowHighlight: (claim) => {
                                if (claim.claim_status === 'INVESTIGATING' && !!claim.investigating_at) {
                                    const NOW = new Date().getTime();
                                    const INVESTIGATING = new Date(claim.investigating_at).getTime();
                                    const ONE_DAY = 8.64e7;

                                    let color;
                                    if (NOW - INVESTIGATING > ONE_DAY * 4) {
                                        color = colors.golds.highlight;
                                    }
                                    if (NOW - INVESTIGATING > ONE_DAY * 5) {
                                        color = colors.reds.highlight;
                                    }

                                    return color;
                                }

                                return null;
                            },
                        }}
                    />
                ) : (
                    <Grid
                        item
                        css={css`
                            width: 100%;
                            height: 100%;
                            display: flex;
                            justify-content: center;
                            align-items: center;
                        `}
                    >
                        <Body1>No claims found</Body1>
                    </Grid>
                )}
            </Grid>
        </Grid>
    );
};

const withContext = (Component) => (props) =>
    (
        <ContextProvider>
            <Component {...props} />
        </ContextProvider>
    );

export default compose(withContext)(Claims);
